import {
  type OutlookRecipient,
  readOutlookMessageFromAddress,
  readOutlookMessageRecipients,
  readOutlookMessageSubject,
} from './EmailScreen/Content/EmailRecipients/useOutlookEmailRecipients';
import {EXTENSION_CONTENT_TYPES} from './outlookComposeItem';
import {getOutlookMailboxUserEmail} from './outlookMailboxEmail';
import type {MessageAttachment} from './SendModal/EmailSync/structs';

export type ReadMessageMetadata = {
  fromAddress: string;
  subject: string;
  recipients: OutlookRecipient[];
  internetMessageId: string | null;
  microsoftMessageId: string | null;
  microsoftConversationId: string | null;
  sentAt: string | null;
  inboxEmailAddress: string | null;
  attachments: MessageAttachment[];
};

export type ReadAttachmentContent = {
  id: string;
  fileName: string;
  fileContentType: string | null;
  content: string;
};

const getMailboxItem = (): any | null => {
  if (typeof Office === 'undefined') {
    return null;
  }
  const item = Office.context?.mailbox?.item;
  return item?.itemType === Office.MailboxEnums.ItemType.Message ? item : null;
};

const isReadModeMessage = (item: any | null): boolean => {
  return Array.isArray(item?.to);
};

const toRestId = (id: string): string => {
  try {
    const mailbox = Office.context.mailbox;
    return typeof mailbox.convertToRestId === 'function'
      ? mailbox.convertToRestId(id, Office.MailboxEnums.RestVersion.v2_0)
      : id;
  } catch {
    return id;
  }
};

const restMessageId = (item: any): string | null => {
  const itemId = item?.itemId;
  if (!itemId || typeof itemId !== 'string') {
    return null;
  }
  return toRestId(itemId);
};

const sentAtIso = (item: any): string | null => {
  const created = item?.dateTimeCreated;
  return created instanceof Date && !Number.isNaN(created.getTime())
    ? created.toISOString()
    : null;
};

const restConversationId = (item: any): string | null => {
  const id = item?.conversationId;
  if (typeof id !== 'string' || !id.trim()) {
    return null;
  }
  return toRestId(id.trim());
};

const guessContentTypeFromFileName = (fileName: string): string | null => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  if (!extension) {
    return null;
  }
  return EXTENSION_CONTENT_TYPES[extension] ?? null;
};

const isFileAttachment = (raw: any): boolean => {
  const attachmentType = raw?.attachmentType;
  if (attachmentType == null) {
    return true;
  }
  return attachmentType === Office.MailboxEnums.AttachmentType.File;
};

const normalizeReadFileAttachment = (raw: any): MessageAttachment | null => {
  if (!isFileAttachment(raw) || raw.isInline) {
    return null;
  }
  const name = raw.name?.trim();
  const id = raw.id?.trim() || name;
  if (!name || !id) {
    return null;
  }
  return {
    id,
    name,
    contentType: guessContentTypeFromFileName(name),
    size: raw.size ?? 0,
  };
};

export const readReadMessageFileAttachments = (): Promise<
  MessageAttachment[]
> => {
  const item = getMailboxItem();
  if (!isReadModeMessage(item)) {
    return Promise.resolve([]);
  }

  if (Array.isArray(item.attachments) && item.attachments.length > 0) {
    return Promise.resolve(
      item.attachments
        .map(normalizeReadFileAttachment)
        .filter(
          (att: MessageAttachment | null): att is MessageAttachment =>
            att != null
        )
    );
  }

  return new Promise((resolve) => {
    if (typeof item.getAttachmentsAsync !== 'function') {
      resolve([]);
      return;
    }
    item.getAttachmentsAsync((result: any) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        resolve([]);
        return;
      }
      resolve(
        (result.value ?? [])
          .map(normalizeReadFileAttachment)
          .filter(
            (att: MessageAttachment | null): att is MessageAttachment =>
              att != null
          )
      );
    });
  });
};

export const readReadMessageMetadata =
  async (): Promise<ReadMessageMetadata | null> => {
    const item = getMailboxItem();
    if (!isReadModeMessage(item)) {
      return null;
    }

    const [fromAddress, subject, recipients, attachments] = await Promise.all([
      readOutlookMessageFromAddress(),
      readOutlookMessageSubject(),
      readOutlookMessageRecipients(),
      readReadMessageFileAttachments(),
    ]);

    return {
      fromAddress,
      subject,
      recipients,
      internetMessageId: item.internetMessageId?.trim() || null,
      microsoftMessageId: restMessageId(item),
      microsoftConversationId: restConversationId(item),
      sentAt: sentAtIso(item),
      inboxEmailAddress: getOutlookMailboxUserEmail() ?? null,
      attachments,
    };
  };

export const readMessageHtmlBody = (): Promise<string | null> => {
  const item = getMailboxItem();
  if (!isReadModeMessage(item) || typeof item.body?.getAsync !== 'function') {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    item.body.getAsync(Office.CoercionType.Html, (result: any) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        resolve(null);
        return;
      }
      const value = typeof result.value === 'string' ? result.value : null;
      resolve(value?.trim() ? value : null);
    });
  });
};

export const readAttachmentContentBase64 = (
  attachmentId: string
): Promise<string | null> => {
  const item = getMailboxItem();
  if (
    !isReadModeMessage(item) ||
    typeof item.getAttachmentContentAsync !== 'function'
  ) {
    return Promise.resolve(null);
  }

  const id = attachmentId.trim();
  if (!id) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    item.getAttachmentContentAsync(id, (result: any) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        resolve(null);
        return;
      }
      const format = result.value?.format;
      const content = result.value?.content;
      if (
        format !== Office.MailboxEnums.AttachmentContentFormat.Base64 ||
        typeof content !== 'string' ||
        !content
      ) {
        resolve(null);
        return;
      }
      resolve(content);
    });
  });
};

export const readAllFileAttachmentContents = async (
  attachments: readonly MessageAttachment[]
): Promise<ReadAttachmentContent[]> => {
  const contents: ReadAttachmentContent[] = [];

  for (const meta of attachments) {
    const content = await readAttachmentContentBase64(meta.id);
    if (!content) {
      continue;
    }
    contents.push({
      id: meta.id,
      fileName: meta.name,
      fileContentType: meta.contentType,
      content,
    });
  }

  return contents;
};
